/** The assessment: a request for a written recommendation on one workload. Free, the form sends the request
 *  and thanks the visitor in place. Paid, the price sits beside the button; paying sends the visitor to Stripe
 *  and Stripe sends them back here (?paid=…&session=…), where the server says whether it went through. Nothing
 *  about a card is seen by this site. Which of the two is the administrator's (Admin > Assessment).
 *  Either way a short call comes before the assessment: the request is acknowledged saying so, the team emails a
 *  link (?call=…) with a few times, and this page, opened from that link, lets the person choose one. */
import { useEffect, useState } from 'react'
import { api } from '../api'
import { useSettings, useSite } from '../settings'
import type { CallInfo } from '../types'
import { track } from './track'
import { usd } from './ui'
import { useAsync } from './useAsync'

type Go = (v: 'calculator' | 'compare' | 'fit' | 'privacy' | 'terms' | 'contact') => void
const CONTEXT_KEY = 'assessment-context'
const FROM_KEY = 'assessment-from'

/** The comparison a tool page hands over when its strip is clicked: one line, in words. */
export function rememberAssessmentContext(context: string, from: string) {
  try { sessionStorage.setItem(CONTEXT_KEY, context); sessionStorage.setItem(FROM_KEY, from) } catch { /* storage blocked */ }
}

function fromQuery(): { paid?: number; session?: string; cancelled?: number; call?: string } {
  const q = new URLSearchParams(window.location.search)
  const paid = Number(q.get('paid')); const cancelled = Number(q.get('cancelled'))
  return { paid: paid > 0 ? paid : undefined, session: q.get('session') ?? undefined, cancelled: cancelled > 0 ? cancelled : undefined,
    call: q.get('call') || undefined }
}

const day = (epoch: number) => new Date(epoch * 1000).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
const clock = (epoch: number) => new Date(epoch * 1000).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })

/** Behind the emailed link: the times offered for the call, in the visitor's own time zone. One is chosen and the
 *  call is set (both sides get a calendar invitation), or none suits and a line says what would. */
function CallPicker({ token, go, contact }: { token: string; go: Go; contact?: string }) {
  const info = useAsync(() => api.assessmentCall(token), [token])
  const [after, setAfter] = useState<CallInfo | null>(null)
  const [pick, setPick] = useState<number | null>(null)
  const [none, setNone] = useState(false)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const d = after ?? info.data
  const zone = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g, ' ') } catch { return 'your time zone' } })()
  const send = async (b: { slot?: number; none?: boolean; note?: string }) => {
    setBusy(true); setError('')
    try { setAfter(await api.chooseCall(token, b)) }
    catch (err) { setError(err instanceof Error ? err.message : String(err)) }
    finally { setBusy(false) }
  }
  if (info.loading && !d) return <p className="muted">Looking up the times…</p>
  if (!d) {
    return (
      <div className="sent" role="status">
        <h3>This link does not open a call.</h3>
        <p>It is not one of ours, or the times it offered have been replaced by a newer email. Use the link in the
          latest one{contact ? <>, or write to <a href={`mailto:${contact}`} className="contactmail">{contact}</a></> : null}.</p>
      </div>
    )
  }
  if (d.state === 'set' && d.chosen) {
    return (
      <div className="sent" role="status">
        <h3>The call is set, {d.name}.</h3>
        <p className="callwhen"><b>{day(d.chosen)}</b><span>{clock(d.chosen)} to {clock(d.chosen + d.minutes * 60)} · {zone}</span></p>
        <p>A calendar invitation is on its way to <b>{d.email}</b>.
          {d.link ? <> The call is on Microsoft Teams: <a href={d.link} target="_blank" rel="noreferrer">join the meeting</a> at that time.</>
            : <> We will send the Microsoft Teams link before the call.</>}</p>
        <p className="muted small">If the time stops suiting you, reply to our email and we will find another.</p>
        <button type="button" className="linklike" onClick={() => go('calculator')}>Back to the Calculator</button>
      </div>
    )
  }
  if (d.state === 'declined') {
    return (
      <div className="sent" role="status">
        <h3>Thank you, {d.name}.</h3>
        <p>We have your note that none of the times suits. We will write to <b>{d.email}</b> with others.</p>
      </div>
    )
  }
  const open = d.slots.filter((x) => !x.past)
  return (
    <>
      <p className="lede">
        Hello {d.name}. Before we write your assessment we would like a {d.minutes}-minute call on Microsoft Teams to
        understand the workload. Choose the time that suits you.
      </p>
      <p className="muted small">Times are shown in your own time zone, {zone}.</p>
      <div className="callslots" role="radiogroup" aria-label="Times for the call">
        {d.slots.map((x) => (
          <button key={x.at} type="button" role="radio" aria-checked={pick === x.at} disabled={x.past || busy}
            className={pick === x.at ? 'slot on' : 'slot'} onClick={() => { setPick(x.at); setNone(false) }}>
            <b>{day(x.at)}</b>
            <span>{x.past ? 'this time has passed' : `${clock(x.at)} to ${clock(x.at + d.minutes * 60)}`}</span>
          </button>
        ))}
      </div>
      {!open.length ? <p className="note small">Every time we offered has passed. Tell us below what would suit you.</p> : null}
      {none || !open.length ? (
        <form className="contactform" onSubmit={(e) => { e.preventDefault(); void send({ none: true, note }) }}>
          <label>What would suit you
            <textarea rows={3} value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)}
              placeholder="Days and hours that work for you, and your time zone." />
          </label>
          <div className="row">
            <button className="primary" type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send'}</button>
            {open.length ? <button type="button" className="linklike" disabled={busy} onClick={() => setNone(false)}>back to the times</button> : null}
          </div>
        </form>
      ) : (
        <div className="row callgo">
          <button className="primary" type="button" disabled={busy || pick == null} onClick={() => pick != null && void send({ slot: pick })}>
            {busy ? 'Setting the call…' : 'Confirm this time'}
          </button>
          <button type="button" className="linklike" disabled={busy} onClick={() => { setNone(true); setPick(null) }}>None of these suits me</button>
        </div>
      )}
      {error ? <p className="err small" role="alert">{error}</p> : null}
    </>
  )
}

export function AssessmentPage({ go }: { go: Go }) {
  const profile = useSite()
  const { assessment, site } = useSettings()
  const [back] = useState(fromQuery)
  const [form, setForm] = useState(() => {
    let context = ''
    try { context = sessionStorage.getItem(CONTEXT_KEY) ?? '' } catch { /* storage blocked */ }
    return { name: '', email: '', company: '', brief: '', context }
  })
  const [trap, setTrap] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  // a free request, sent: who to thank and where the assessment will go
  const [sent, setSent] = useState<{ name: string; email: string } | null>(null)
  useEffect(() => {
    let from = 'direct'
    try { from = sessionStorage.getItem(FROM_KEY) || 'direct'; sessionStorage.removeItem(FROM_KEY) } catch { /* storage blocked */ }
    if (!back.paid && !back.cancelled && !back.call) track('assessment_open', from)
  }, [back.paid, back.cancelled, back.call])
  // back from Stripe: the server says whether the payment went through
  const status = useAsync(
    () => (back.paid && back.session ? api.assessmentStatus(back.paid, back.session) : Promise.resolve(null)),
    [back.paid, back.session],
  )
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => { setForm({ ...form, [k]: e.target.value }); setError('') }
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) { setError('Tell us your name'); return }
    if (!form.email.trim()) { setError('Enter your email address, so we can send the assessment'); return }
    if (!form.brief.trim()) { setError('Tell us what to look at'); return }
    setBusy(true); setError('')
    try {
      if (assessment?.free) {
        const r = await api.assessmentRequest({ ...form, website: trap })
        try { sessionStorage.removeItem(CONTEXT_KEY) } catch { /* storage blocked */ }
        setSent({ name: r.name, email: r.email }); setBusy(false)
        return
      }
      const r = await api.assessmentCheckout({ ...form, website: trap })
      try { sessionStorage.removeItem(CONTEXT_KEY) } catch { /* storage blocked */ }
      window.location.assign(r.url)                       // Stripe's page; it sends the visitor back here
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); setBusy(false) }
  }
  const price = assessment?.price_usd
  const free = Boolean(assessment?.enabled && assessment.free)
  return (
    <article className="legal panel company">
      <h2>{back.call ? 'A call about your assessment' : <>Request an assessment{free ? <> <span className="tag freetag">Free</span></> : null}</>}</h2>
      {back.call ? <CallPicker token={back.call} go={go} contact={site?.contact_email} /> : sent ? (
        <div className="sent" role="status">
          <h3>Thank you, {sent.name}.</h3>
          <p>Your request is with us and we are reviewing it. Before we write the assessment we would like a short
            online call to understand the workload: we will write to <b>{sent.email}</b> with a few times to choose
            from, on Microsoft Teams. It is free: there is nothing to pay.</p>
          <button type="button" className="linklike" onClick={() => go('calculator')}>Back to the Calculator</button>
        </div>
      ) : back.paid ? (
        status.loading ? <p className="muted">Checking your payment…</p>
        : status.data?.status === 'paid' || status.data?.status === 'delivered' ? (
          <div className="sent" role="status">
            <h3>Thank you, {status.data.name}.</h3>
            <p>Your payment of <b>{usd(status.data.amount_usd, 0)}</b> went through and your request is with us. We are
              reviewing it. Before we write the assessment we would like a short online call to understand the
              workload: we will write to <b>{status.data.email}</b> with a few times to choose from, on Microsoft
              Teams. Stripe sends the receipt.</p>
            <button type="button" className="linklike" onClick={() => go('calculator')}>Back to the Calculator</button>
          </div>
        ) : (
          <div className="sent" role="status">
            <h3>We have your request, but not a payment yet.</h3>
            <p>If you paid a moment ago, the confirmation can take a minute to reach us; reload this page. If the
              payment did not go through, nothing was charged
              {site?.contact_email ? <>; write to <a href={`mailto:${site.contact_email}`} className="contactmail">{site.contact_email}</a> if you are unsure</> : null}.</p>
          </div>
        )
      ) : !assessment?.enabled || (!free && !price) ? (
        <p className="lede">Assessments are not offered just now. The <button type="button" className="linklike" onClick={() => go('contact')}>contact form</button> is open for questions.</p>
      ) : (
        <>
          <p className="lede">
            The site prices list rates for one model and one workload. An assessment is the next step: tell us your real
            volumes, constraints and the clouds you can use, and the {profile.name} team writes a recommendation for one
            workload, with the figures and where they come from{free ? <>. It is <b>free</b>: there is nothing to pay.</>
              : <>, for a fixed fee of <b>{usd(price ?? 0, 0)}</b>.</>}
          </p>
          <ol className="assesssteps">
            <li><b>You send the request.</b> We confirm it by email.</li>
            <li><b>We talk.</b> A short call on Microsoft Teams to understand the workload, at a time you choose.</li>
            <li><b>We write it.</b> The assessment comes to you by email.</li>
          </ol>
          {back.cancelled ? <p className="note small">The payment page was closed before paying; nothing was charged. Your request below was not sent.</p> : null}
          <form className="contactform" onSubmit={submit} noValidate>
            <div className="two">
              <label>Your name<input type="text" value={form.name} maxLength={120} autoComplete="name" onChange={set('name')} /></label>
              <label>Work email<input type="email" value={form.email} maxLength={254} autoComplete="email" placeholder="you@company.com" onChange={set('email')} /></label>
            </div>
            <label>Company (optional)<input type="text" value={form.company} maxLength={120} autoComplete="organization" onChange={set('company')} /></label>
            {form.context ? (
              <label>What you were looking at
                <input type="text" value={form.context} maxLength={1000} onChange={set('context')} />
              </label>
            ) : null}
            <label>What should we look at
              <textarea rows={7} value={form.brief} maxLength={4000} onChange={set('brief')}
                placeholder="The workload (tokens a day, peak hours, context length), where the data must stay, the clouds you have agreements with, and what the site's numbers do not capture." />
            </label>
            <input className="trap" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={trap} onChange={(e) => setTrap(e.target.value)} />
            <div className="row">
              <button className="primary" type="submit" disabled={busy}>
                {free ? (busy ? 'Sending…' : 'Request the assessment') : busy ? 'Opening the payment page…' : `Pay ${usd(price ?? 0, 0)} and request`}
              </button>
              <span className="muted small">{form.brief.length} / 4000</span>
            </div>
            {error ? <p className="err small" role="alert">{error}</p> : null}
            <p className="muted small consent">
              {free ? 'The assessment is free and no card is asked for. '
                : "Paying opens Stripe's page, which takes the card; this site never sees it. "}
              We keep your request and your address to deliver the assessment, and for nothing else. See the{' '}
              <button type="button" className="linklike" onClick={() => go('privacy')}>privacy policy</button> and the{' '}
              <button type="button" className="linklike" onClick={() => go('terms')}>terms</button>.
            </p>
          </form>
        </>
      )}
      {site?.operator ? <p className="small muted">{profile.name} is run by {site.operator}{site.jurisdiction ? `, ${site.jurisdiction}` : ''}.</p> : null}
    </article>
  )
}

/** The strip under a tool's comparison: one line and a button to the assessment page, carrying the comparison. */
export function AssessmentStrip({ context, from, go, headline }: { context: string; from: string; go: () => void; headline?: string }) {
  const { assessment } = useSettings()
  if (!assessment?.enabled || (!assessment.free && !assessment.price_usd)) return null
  return (
    <section className="assessstrip" aria-label="Assessment" data-tour="assessment">
      <span className="lbl">Assessment</span>
      <span className="headline">{headline ?? 'Have this comparison checked against your real workload, with a written recommendation.'}</span>
      <button type="button" className="pill action" onClick={() => { rememberAssessmentContext(context, from); go() }}>
        Request an assessment · {assessment.free ? 'Free' : usd(assessment.price_usd ?? 0, 0)}
      </button>
    </section>
  )
}
